import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  PriorityLegend,
  SharedGantt,
  SharedTree,
  ViewToggle,
  type SnapshotResponse,
  type ViewMode,
} from "../components/sharedTaskRender";

// Public read-only view of a shared scope.  No auth, no Replicache — just
// fetches the snapshot JSON from /api/shared/:token and renders both a
// tree and a Gantt visualisation; the viewer picks which to see via a
// header toggle.  All visualisations are read-only — interactive shares
// (read_tick / read_comment / edit) are still a future-phase feature.
//
// The actual tree/Gantt renderers live in ../components/sharedTaskRender so
// the Excalidraw embed page (EmbedView) can reuse them verbatim.

type LoadState =
  | { status: "loading" }
  | { status: "ok"; data: SnapshotResponse }
  | { status: "error"; message: string };

const VIEW_KEY = "johndo:sharedView";

export function SharedView({
  loader,
  loaderKey,
}: {
  /** Override the default token-based fetch.  When provided, the URL
   *  `:token` param is ignored — used by /shared-with-me/:id which has
   *  to send an Authorization header and hit a different endpoint. */
  loader?: () => Promise<SnapshotResponse>;
  /** When the loader's identity changes between renders, pass a stable
   *  key (e.g. the share id) so the effect refires on switch. */
  loaderKey?: string;
} = {}) {
  const params = useParams<{ token: string }>();
  const token = params.token;
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [view, setView] = useState<ViewMode>(() => {
    const stored =
      typeof window === "undefined" ? null : localStorage.getItem(VIEW_KEY);
    return stored === "gantt" ? "gantt" : "tree";
  });
  useEffect(() => {
    localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  useEffect(() => {
    let cancelled = false;
    if (!loader && !token) {
      setState({ status: "error", message: "Missing token in URL." });
      return;
    }
    (async () => {
      try {
        let data: SnapshotResponse;
        if (loader) {
          data = await loader();
        } else {
          const res = await fetch(`/api/shared/${encodeURIComponent(token!)}`);
          if (!res.ok) {
            const body = (await res.json().catch(() => null)) as
              | { error?: string }
              | null;
            const msg =
              res.status === 401
                ? "This link is invalid or has expired."
                : res.status === 403
                  ? "This link has been revoked."
                  : body?.error ?? `HTTP ${res.status}`;
            if (!cancelled) setState({ status: "error", message: msg });
            return;
          }
          data = (await res.json()) as SnapshotResponse;
        }
        if (!cancelled) setState({ status: "ok", data });
      } catch (err) {
        if (!cancelled)
          setState({
            status: "error",
            message: err instanceof Error ? err.message : String(err),
          });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, loaderKey]);

  if (state.status === "loading") {
    return (
      <main className="mx-auto flex min-h-full max-w-3xl items-center justify-center bg-white p-8 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading shared view…</p>
      </main>
    );
  }
  if (state.status === "error") {
    return (
      <main className="mx-auto flex min-h-full max-w-2xl flex-col items-center justify-center gap-4 bg-white p-8 text-center dark:bg-slate-900">
        <h1 className="text-xl font-semibold tracking-tight">
          Can't open this share
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-300">{state.message}</p>
      </main>
    );
  }

  const data = state.data;
  const title =
    data.scope === "group"
      ? data.group?.name ?? "Shared group"
      : data.rootItem?.title || "Shared item";

  return (
    <div className="flex min-h-full flex-col bg-white dark:bg-slate-900">
      <header className="border-b border-slate-200 bg-white px-4 py-3 sm:px-6 sm:py-4 dark:border-slate-700 dark:bg-slate-900">
        <div className="mx-auto flex max-w-5xl flex-wrap items-baseline justify-between gap-y-2 gap-x-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
              {title}
            </h1>
            {data.scope === "item" && data.group && (
              <p className="text-xs text-slate-500 dark:text-slate-400">in {data.group.name}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <ViewToggle view={view} onView={setView} />
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              Shared · {data.permissions}
            </span>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl p-4 sm:p-6">
        {data.items.length === 0 ? (
          <p className="text-sm italic text-slate-500 dark:text-slate-400">
            Nothing to show — the shared scope is empty.
          </p>
        ) : view === "tree" ? (
          <SharedTree data={data} />
        ) : (
          <SharedGantt data={data} />
        )}

        <PriorityLegend />

        <footer className="mt-6 border-t border-slate-200 pt-4 text-xs text-slate-400 dark:border-slate-700 dark:text-slate-500">
          Read-only view. Powered by Seneca.
        </footer>
      </main>
    </div>
  );
}
